import CiroContainer from "../../components/shared/CiroContainer";
import classNames from "classnames";
import CiroTitle from "../../components/shared/CiroTitle";
import CiroButton, {
  CiroButtonStyleEnum,
} from "../../components/shared/CiroButton";
import {
  Team_OrganizationUserAccount,
  Team_UserInvitePermissions,
  TeamMembersTable,
} from "../../components/team/TeamMembersTable";
import { gql, useQuery } from "@apollo/client";
import {
  CreditPlanEnum,
  Team_UserAccountQuery,
  Team_UserAccountQueryVariables,
  Team_UserInvitePermissionsFragmentDoc,
  Team_OrganizationUserAccountFragmentDoc,
} from "../../__generated__/graphql";
import {
  PRICING_PAGE_LINK,
  STRIPE_CUSTOMER_PORTAL_LINK,
} from "../../utils/consts";
import { useState } from "react";
import { TeamMemberInviteReferModal } from "../../components/team/TeamMemberInviteReferModal";
import { useFragment as getFragmentData } from "../../__generated__";

export const Team_UserAccount = gql`
  query Team_UserAccount {
    userAccount {
      pk
      org {
        paid_licenses
        userAccounts {
          ...Team_OrganizationUserAccount
        }
        credit_plan {
          plan
        }
      }
    }
    permissions {
      ...Team_UserInvitePermissions
    }
  }
  ${Team_OrganizationUserAccount}
  ${Team_UserInvitePermissions}
`;

export const TeamSettings = () => {
  const { data: userAccountData, loading: userAccountLoading } = useQuery<
    Team_UserAccountQuery,
    Team_UserAccountQueryVariables
  >(Team_UserAccount);

  const [showInviteModal, setShowInviteModal] = useState(false);

  const creditPlan = userAccountData?.userAccount?.org?.credit_plan;
  const isRecurringPlan =
    creditPlan?.plan === CreditPlanEnum.Monthly ||
    creditPlan?.plan === CreditPlanEnum.Annual;

  const managementLinkHref = isRecurringPlan
    ? STRIPE_CUSTOMER_PORTAL_LINK
    : PRICING_PAGE_LINK;

  const activeUsers = userAccountData?.userAccount?.org?.userAccounts || [];

  const totalUserCount = activeUsers.length;
  const totalLicenseCount =
    userAccountData?.userAccount?.org?.paid_licenses || 1;
  const allLicensesFilled = totalUserCount === totalLicenseCount;
  const overLicenseCount = totalUserCount > totalLicenseCount;

  const handleModalClose = () => {
    setShowInviteModal(false);
  };

  return (
    <CiroContainer className={classNames("bg-zinc-100")}>
      <TeamMemberInviteReferModal
        canInviteUsers={getFragmentData(Team_UserInvitePermissionsFragmentDoc, userAccountData?.permissions)?.hasTeamManagement}
        overInviteLimit={allLicensesFilled || overLicenseCount}
        isOpen={showInviteModal}
        onClose={handleModalClose}
      />
      <div
        className={classNames(
          "flex",
          "justify-between",
          "mb-3",
          "sm:mb-6",
          "text-left",
          "w-full",
          "flex-col",
          "sm:flex-row",
        )}
      >
        <CiroTitle
          title={
            <div
              className={classNames(
                "flex",
                "flex-row",
                "gap-2",
                "items-center",
                "mt-4",
              )}
            >
              <span>Team</span>
            </div>
          }
        />
      </div>
      <div
        className={classNames(
          "flex",
          "w-full",
          "justify-between",
          "items-center",
        )}
      >
        <div className={classNames("text-sm ")}>
          <span
            className={classNames("font-semibold", "mr-1", {
              "text-green-600": !overLicenseCount,
              "text-red-600": overLicenseCount,
            })}
          >
            {totalUserCount} / {totalLicenseCount}
          </span>
          Ciro licenses
        </div>
        <div className={classNames("flex", "gap-2")}>
          <CiroButton
            analyticsField="Navigate to manage subscription or pricing"
            onClick={() => {
              window.location.href = managementLinkHref;
            }}
          >
            Manage subscription
          </CiroButton>
          <CiroButton
            analyticsField="Invite or refer team members"
            style={CiroButtonStyleEnum.LOUD}
            onClick={() => {
              setShowInviteModal(true);
            }}
          >
            + Invite people
          </CiroButton>
        </div>
      </div>

      <div className={classNames("overflow-auto", "w-full", "my-4")}>
        <TeamMembersTable
          userAccountsData={getFragmentData(
            Team_OrganizationUserAccountFragmentDoc,
            userAccountData?.userAccount?.org?.userAccounts,
          )}
          userAccountLoading={userAccountLoading}
          permissions={getFragmentData(
            Team_UserInvitePermissionsFragmentDoc,
            userAccountData?.permissions,
          )}
        />
      </div>
    </CiroContainer>
  );
};
